import React, { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../app/store';
import Optional from '../../Optional/Optional';
import { useJwtHeaders } from '../../../hooks/useJwtHeaders';
import axios from 'axios';
import { saveExtraAction } from '../../../actions/saveExtraAction';
import './CreateExtrasPage.scss';

interface ExtraItem {
  id: number;
  kind: string;
  price: number | string;
  year?: string;
  active?: boolean;
}

const CreateExtrasPage: FC = () => {
  const dispatch = useDispatch();
  const headers = useJwtHeaders();
  const admin = useSelector<RootState, boolean>(({ user }) => user.admin!);

  const [extras, setExtras] = useState<ExtraItem[]>([]);
  const [kind, setKind] = useState<string>('');
  const [price, setPrice] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);

  const fetchExtras = () => {
    axios
      .get('/api/v1/admin/extras', headers)
      .then(({ data }) => {
        setExtras(data);
      })
      .catch(() => {
        axios.get('/api/v1/available-items', headers).then(({ data }) => {
          if (data && data.extras) {
            setExtras(data.extras);
          }
        });
      });
  };

  useEffect(() => {
    fetchExtras();
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!kind.trim() || !price) return;

    setLoading(true);
    dispatch(
      saveExtraAction(kind, price, headers, () => {
        setKind('');
        setPrice('');
        setLoading(false);
        fetchExtras();
      })
    );
  };

  const handleToggleActive = (id: number, newActive: boolean) => {
    setExtras(prev => prev.map(item => (item.id === id ? { ...item, active: newActive } : item)));
    axios.patch(`/api/v1/admin/extras/${id}`, { active: newActive }, headers).catch(() => {
      setExtras(prev => prev.map(item => (item.id === id ? { ...item, active: !newActive } : item)));
    });
  };

  const handleDelete = (id: number) => {
    if (!window.confirm('Are you sure you want to delete this Extra item?')) return;
    setExtras(prev => prev.filter(item => item.id !== id));
    axios.delete(`/api/v1/admin/extras/${id}`, headers).catch(() => {
      alert('Failed to delete Extra item');
      fetchExtras();
    });
  };

  return (
    <div className="container admin-item-page">
      <Optional renderIf={admin}>
        <h3>Admin: Manage & Create Extras</h3>

        <div className="form-section">
          <h5 className="mb-3">Add New Extra Item</h5>
          <form onSubmit={handleSubmit} className="inline-form">
            <div style={{ flex: '1 1 200px', maxWidth: '300px' }}>
              <input
                type="text"
                className="form-control"
                placeholder="Item"
                value={kind}
                onChange={e => setKind(e.target.value)}
                required
              />
            </div>
            <div style={{ width: '120px' }}>
              <input
                type="text"
                className="form-control"
                placeholder="Price ($)"
                value={price}
                onChange={e => setPrice(e.target.value.replace(/[^0-9]/, ''))}
                required
              />
            </div>
            <div className="submit-btn-container">
              <button
                type="submit"
                className="btn btn-success"
                disabled={!kind.trim() || !price || loading}>
                {loading ? 'Saving...' : 'Save Extra'}
              </button>
            </div>
          </form>
        </div>

        <h5>Current Extra Items ({extras.length})</h5>
        <table className="table table-hover table-striped">
          <thead>
            <tr>
              <th>ID</th>
              <th>Kind</th>
              <th>Price</th>
              <th>Year</th>
              <th>Active</th>
              <th style={{ width: '50px' }}></th>
            </tr>
          </thead>
          <tbody>
            {extras.map(extra => (
              <tr key={`extra-${extra.id}`}>
                <td>{extra.id}</td>
                <td>{extra.kind}</td>
                <td>${extra.price}</td>
                <td>{extra.year || new Date().getFullYear()}</td>
                <td>
                  <input
                    type="checkbox"
                    checked={extra.active ?? true}
                    onChange={e => handleToggleActive(extra.id, e.target.checked)}
                  />
                </td>
                <td className="text-center">
                  <i
                    className="fa delete-icon"
                    title="Delete Extra"
                    onClick={() => handleDelete(extra.id)}>
                    &#xf014;
                  </i>
                </td>
              </tr>
            ))}
            {extras.length === 0 && (
              <tr>
                <td colSpan={6} className="text-center text-muted">
                  No Extras found for current year.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Optional>
      <Optional renderIf={!admin}>
        <b>YOU MUST BE AN ADMIN TO VIEW THIS PAGE</b>
      </Optional>
    </div>
  );
};

export default CreateExtrasPage;
